import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "telltag_admin_token";
export const DOOR_KEY = "telltag_inbox_door";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function Inbox() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    if (sessionStorage.getItem(DOOR_KEY) !== "1") navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/contact`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMessages(r.data))
      .catch(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, r.data.access_token);
      setToken(r.data.access_token);
      setPassword("");
    } catch {
      setError("Incorrect password.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMessages(null);
  };

  const toggleRead = async (m) => {
    const r = await axios.patch(`${API}/contact/${m.id}/read`, { read: !m.read }, { headers: { Authorization: `Bearer ${token}` } });
    setMessages((list) => list.map((x) => (x.id === m.id ? r.data : x)));
  };

  const replyHref = (m) =>
    `mailto:${m.email}?subject=${encodeURIComponent("Re: your message to Telltag")}&body=${encodeURIComponent(`Hi ${m.name},\n\n\n\n—\n\n> ${m.message.replace(/\n/g, "\n> ")}`)}`;

  const unread = messages ? messages.filter((m) => !m.read).length : 0;

  const topics = messages ? [...new Set(messages.map((m) => m.topic).filter(Boolean))] : [];
  const visible = messages ? (filter === "All" ? messages : messages.filter((m) => m.topic === filter)) : null;
  const countFor = (t) => (t === "All" ? messages.length : messages.filter((m) => m.topic === t).length);

  return (
    <main className="on-dark min-h-screen bg-[#0D1B2A] text-[#F2F3F5] px-6 md:px-10 py-8" data-testid="inbox-page">
      <header className="flex items-center justify-between">
        <a href="/" className="display text-[0.95rem]" style={{ letterSpacing: "0.18em" }} data-testid="inbox-wordmark">
          TELLTAG
        </a>
        <span className="eyebrow text-[#F2F3F5]/70">Inbox</span>
      </header>

      {!token ? (
        <form onSubmit={login} className="mt-[22vh] max-w-sm" data-testid="inbox-login-form">
          <label className="block">
            <span className="eyebrow text-[#F2F3F5]/70">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              className="field mt-2"
              data-testid="inbox-password-input"
            />
          </label>
          {error && (
            <p className="mono text-xs text-[#E74C3C] mt-4" data-testid="inbox-error">
              {error}
            </p>
          )}
          <button type="submit" disabled={busy} className="btn-sharp text-[#F2F3F5] mt-8" style={{ "--btn-hover-text": "#0D1B2A" }} data-testid="inbox-login-button">
            <span>{busy ? "Checking" : "Enter"}</span>
            <span aria-hidden>→</span>
          </button>
        </form>
      ) : (
        <section className="mt-16 md:mt-24">
          <div className="flex items-baseline justify-between border-b border-[#F2F3F5]/20 pb-5">
            <h1 className="display text-[2rem] md:text-5xl" data-testid="inbox-headline">
              Messages
            </h1>
            <div className="flex items-center gap-6">
              <span className="mono text-xs text-[#F2F3F5]/70" data-testid="inbox-count">
                {messages ? `${unread} new / ${messages.length}` : "—"}
              </span>
              <button onClick={logout} className="eyebrow text-[#F2F3F5]/70 hover:text-[#F2F3F5] transition-colors" data-testid="inbox-logout-button">
                Sign out
              </button>
            </div>
          </div>

          {messages && messages.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-6" data-testid="inbox-topic-filters">
              {["All", ...topics].map((t) => {
                const active = filter === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFilter(t)}
                    className="eyebrow py-2 transition-colors duration-300 border-b"
                    style={{
                      color: active ? "#F2F3F5" : "rgba(242,243,245,0.55)",
                      borderColor: active ? "#6B3FA0" : "transparent",
                    }}
                    data-testid={`inbox-filter-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    data-active={active ? "true" : "false"}
                  >
                    {t} <span className="mono text-[0.65rem] opacity-70">{countFor(t)}</span>
                  </button>
                );
              })}
            </div>
          )}

          {messages && messages.length === 0 && (
            <p className="body-copy mt-10" data-testid="inbox-empty">
              Nothing yet.
            </p>
          )}

          {visible && messages.length > 0 && visible.length === 0 && (
            <p className="body-copy mt-10" data-testid="inbox-empty-filter">
              No messages in {filter}.
            </p>
          )}

          <ul data-testid="inbox-list">
            {visible?.map((m, i) => (
              <motion.li
                key={m.id}
                className="grid grid-cols-12 gap-x-6 gap-y-4 py-8 border-b border-[#F2F3F5]/15 transition-opacity duration-500"
                style={{ opacity: m.read ? 0.55 : 1 }}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: m.read ? 0.55 : 1, y: 0 }}
                transition={{ duration: 0.6, delay: Math.min(i, 8) * 0.05 }}
                data-testid={`inbox-item-${i}`}
                data-read={m.read ? "true" : "false"}
              >
                <div className="col-span-12 md:col-span-3 flex items-start gap-3">
                  <span
                    className="mt-2 block h-2 w-2 rounded-full shrink-0 transition-colors duration-500"
                    style={{ background: m.read ? "transparent" : "#6B3FA0", boxShadow: m.read ? "inset 0 0 0 1px rgba(242,243,245,0.35)" : "none" }}
                    data-testid={`inbox-item-${i}-status`}
                    aria-label={m.read ? "Read" : "New"}
                  />
                  <div>
                    <p className="text-base font-medium">{m.name}</p>
                    <a href={`mailto:${m.email}`} className="mono text-xs text-[#F2F3F5]/70 hover:text-[#F2F3F5] break-all">
                      {m.email}
                    </a>
                  </div>
                </div>
                <p className="col-span-12 md:col-span-6">
                  {m.topic && (
                    <span className="mono text-[0.6rem] tracking-[0.2em] uppercase text-[#F2F3F5]/60 block mb-2" data-testid={`inbox-item-${i}-topic`}>
                      {m.topic}
                    </span>
                  )}
                  <span className="body-copy whitespace-pre-wrap block">{m.message}</span>
                </p>
                <div className="col-span-12 md:col-span-3 flex md:flex-col md:items-end gap-x-6 gap-y-3">
                  <span className="mono text-xs text-[#F2F3F5]/60">{fmt(m.created_at)}</span>
                  <div className="flex items-center gap-5">
                    <a href={replyHref(m)} className="eyebrow text-[#F2F3F5] underline underline-offset-4 decoration-[#F2F3F5]/40 hover:decoration-[#F2F3F5]" data-testid={`inbox-item-${i}-reply`}>
                      Reply
                    </a>
                    <button
                      type="button"
                      onClick={() => toggleRead(m)}
                      className="eyebrow text-[#F2F3F5]/70 hover:text-[#F2F3F5] transition-colors"
                      data-testid={`inbox-item-${i}-toggle-read`}
                    >
                      {m.read ? "Mark unread" : "Mark read"}
                    </button>
                  </div>
                </div>
              </motion.li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
